import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { requestNotificationPermission, onMessageListener } from '../config/firebase';

interface NotificationContextType {
  notificationToken: string | null;
  requestPermission: () => Promise<string | null>;
  showNotification: (title: string, message: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notificationToken, setNotificationToken] = useState<string | null>(null);

  const requestPermission = async (): Promise<string | null> => {
    try {
      const token = await requestNotificationPermission();
      if (token) {
        setNotificationToken(token);
        // Here you can send the token to your backend to store it
        console.log('Notification token:', token);
      }
      return token;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return null;
    }
  };

  const showNotification = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const toastMessage = (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{title}</div>
        <div>{message}</div>
      </div>
    );

    switch (type) {
      case 'success':
        toast.success(toastMessage);
        break;
      case 'error':
        toast.error(toastMessage);
        break;
      case 'warning':
        toast.warning(toastMessage);
        break;
      default:
        toast.info(toastMessage);
        break;
    }
  };

  useEffect(() => {
    // Request notification permission when the app loads
    requestPermission();

    // Listen for foreground messages
    onMessageListener()
      .then((payload: any) => {
        console.log('Received foreground message:', payload);
        
        // Show toast notification for foreground messages
        const title = payload.notification?.title || 'New Notification';
        const body = payload.notification?.body || 'You have a new notification';
        
        showNotification(title, body, 'info');
      })
      .catch((error) => {
        console.error('Error listening for messages:', error);
      });
  }, []);

  const value: NotificationContextType = {
    notificationToken,
    requestPermission,
    showNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </NotificationContext.Provider>
  );
}; 